import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { InvestigationDrawer, type WorkflowLink } from '@/components/investigation/InvestigationDrawer';
import { Section, Row, NavRow, Empty, InfoRow } from '@/components/investigation/sections';
import { QueueStatusChip, QueuePriorityChip } from '@/features/queue/components/QueueChips';
import { useQueueMessageDetail, useQueueMessageAction } from '@/features/queue/hooks';
import { formatBytes, formatRelative, domainOf } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { QueueMessage, QueueMessageDetail } from '@/features/queue/types';
import { Timeline } from '@/components/investigation/Timeline';
import {
  EnvelopeIcon, AdjustmentsHorizontalIcon, FunnelIcon, FireIcon, BellAlertIcon,
  PaperClipIcon, LinkIcon, ShieldExclamationIcon, ClockIcon, ServerStackIcon,
  CpuChipIcon, ArrowPathIcon, BoltIcon, PlayCircleIcon,
} from '@/icons';


/**
 * Queue message investigation — a thin adapter over the shared InvestigationDrawer.
 * Tabs: Overview / Delivery timeline / SMTP conversation / History (queue+worker+
 * retry) / Related (policy, spam, threat, attachments, URLs, alerts). The workflow
 * quick-nav and Related rows deep-link OUT to Mail Tracking, the Policy Engine,
 * Spam Intelligence, Threat Dashboard, Security Alerts, Attachment and URL
 * Investigation and Quarantine — this surface performs no scanning or policy
 * logic itself. Raw URLs are shown as text, never clickable (may be malicious).
 */
export function QueueDetailsDrawer({ message, isOpen, onClose }: {
  message: QueueMessage | null; isOpen: boolean; onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading } = useQueueMessageDetail(isOpen && message ? message.queueId : null);
  const act = useQueueMessageAction();

  const workflow: WorkflowLink[] | undefined = data ? buildWorkflow(data) : undefined;
  const tabs = data ? [
    { key: 'overview', title: 'Overview', content: <OverviewTab data={data} /> },
    { key: 'delivery', title: 'Delivery timeline', content: <DeliveryTab data={data} /> },
    { key: 'smtp', title: 'SMTP conversation', content: <SmtpTab data={data} /> },
    { key: 'history', title: 'History', content: <HistoryTab data={data} /> },
    { key: 'related', title: 'Related', content: <RelatedTab data={data} onNavigate={(to) => navigate(to)} /> },
  ] : undefined;

  const header = data ? (
    <>
      <QueueStatusChip value={data.status} />
      <QueuePriorityChip value={data.priority} />
      <span className="font-mono text-xs text-fg-subtle">{data.queueId}</span>
      <span className="text-xs text-fg-subtle">{formatBytes(data.sizeBytes)} · {formatRelative(data.arrivalTime)}</span>
    </>
  ) : undefined;

  const footer = message ? (
    <>
      <Button variant="light" onPress={onClose}>Close</Button>
      <Button variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} isLoading={act.isPending}
        onPress={() => act.mutate({ action: 'retry', queueIds: [message.queueId] })}>Retry</Button>
      <Button variant="flat" startContent={<BoltIcon className="h-4 w-4" />} isLoading={act.isPending}
        onPress={() => act.mutate({ action: 'force', queueIds: [message.queueId] })}>Force delivery</Button>
      <Button color="primary" startContent={<PlayCircleIcon className="h-4 w-4" />} isLoading={act.isPending}
        onPress={() => act.mutate({ action: 'release', queueIds: [message.queueId] })}>Release</Button>
    </>
  ) : null;

  return (
    <InvestigationDrawer
      isOpen={isOpen} onClose={onClose}
      title={message ? message.subject || '(no subject)' : 'Queued message'}
      isLoading={!!message && isLoading}
      loadingLabel="Loading message analysis…"
      header={header} workflow={workflow} tabs={tabs}
      onNavigate={(to) => navigate(to)} footer={footer}
    />
  );
}

function buildWorkflow(data: QueueMessageDetail): WorkflowLink[] {
  const senderDomain = domainOf(data.sender);
  return [
    { key: 'mt', label: 'Mail Tracking', icon: <EnvelopeIcon className="h-4 w-4" />, to: data.messageId ? `/mail-tracking?messageId=${encodeURIComponent(data.messageId)}` : '/mail-tracking' },
    { key: 'pol', label: 'Policy Engine', icon: <AdjustmentsHorizontalIcon className="h-4 w-4" />, to: data.policyDecisions[0] ? `/policies?policyId=${encodeURIComponent(data.policyDecisions[0].id)}` : '/policies' },
    { key: 'spam', label: 'Spam Intelligence', icon: <FunnelIcon className="h-4 w-4" />, to: senderDomain ? `/spam-intelligence?domain=${encodeURIComponent(senderDomain)}` : '/spam-intelligence' },
    { key: 'td', label: 'Threat Dashboard', icon: <FireIcon className="h-4 w-4" />, to: '/threat-dashboard' },
    { key: 'sa', label: 'Security Alerts', icon: <BellAlertIcon className="h-4 w-4" />, to: '/security-alerts' },
    { key: 'at', label: 'Attachments', icon: <PaperClipIcon className="h-4 w-4" />, to: data.messageId ? `/attachments?messageId=${encodeURIComponent(data.messageId)}` : '/attachments' },
    { key: 'url', label: 'URL Investigation', icon: <LinkIcon className="h-4 w-4" />, to: senderDomain ? `/url-investigation?domain=${encodeURIComponent(senderDomain)}` : '/url-investigation' },
    { key: 'qz', label: 'Quarantine', icon: <ShieldExclamationIcon className="h-4 w-4" />, to: senderDomain ? `/quarantine?domain=${encodeURIComponent(senderDomain)}` : '/quarantine' },
  ];
}

function OverviewTab({ data }: { data: QueueMessageDetail }) {
  return (
    <div className="space-y-6">
      <Section title="Message" icon={<EnvelopeIcon className="h-4 w-4" />}>
        <dl className="grid gap-1 text-sm sm:grid-cols-2">
          <Row label="From" value={data.sender} />
          <Row label="To" value={data.recipient} />
          <Row label="Message-ID" value={data.messageId} mono />
          <Row label="Tenant" value={data.tenant} />
          {data.originIp && <Row label="Origin IP" value={data.originIp} mono />}
          <Row label="Size" value={formatBytes(data.sizeBytes)} />
        </dl>
      </Section>
      <Section title="Queue state" icon={<ClockIcon className="h-4 w-4" />}>
        <dl className="grid gap-1 text-sm sm:grid-cols-2">
          <Row label="Current queue" value={data.queue} />
          <Row label="Retries" value={String(data.retries)} />
          <Row label="Next retry" value={data.nextRetry ? formatRelative(data.nextRetry) : '—'} />
          <Row label="Worker" value={data.worker ?? '—'} mono />
        </dl>
        {data.reason && (
          <p className="mt-2 rounded-md border border-border bg-danger/5 px-3 py-1.5 text-sm text-danger">{data.reason}</p>
        )}
      </Section>
    </div>
  );
}

function DeliveryTab({ data }: { data: QueueMessageDetail }) {
  return (
    <Section title="Delivery timeline" icon={<ClockIcon className="h-4 w-4" />}>
      <Timeline entries={data.deliveryTimeline.map((e) => ({
        tone: e.status === 'error' ? 'error' as const : e.status === 'warn' ? 'warn' as const : 'ok' as const,
        title: e.label,
        detail: e.detail,
        meta: formatRelative(e.at),
      }))} />
    </Section>
  );
}

function SmtpTab({ data }: { data: QueueMessageDetail }) {
  return (
    <Section title="SMTP conversation" icon={<ServerStackIcon className="h-4 w-4" />}>
      {data.smtpConversation.length === 0 ? <Empty /> : (
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-surface-raised/40 p-3 font-mono text-xs leading-relaxed">
          {data.smtpConversation.map((l, i) => (
            <div key={i} className={cn(l.error ? 'text-danger' : l.direction === 'client' ? 'text-brand' : 'text-success')}>
              {l.direction === 'client' ? 'C: ' : 'S: '}{l.text}
            </div>
          ))}
        </pre>
      )}
    </Section>
  );
}

function HistoryTab({ data }: { data: QueueMessageDetail }) {
  return (
    <div className="space-y-6">
      <Section title="Queue history" icon={<ClockIcon className="h-4 w-4" />}>
        {data.queueHistory.length === 0 ? <Empty /> : (
          <ul className="space-y-1">{data.queueHistory.map((e, i) => (
            <InfoRow key={i}>
              <span className="text-fg">{e.label}{e.detail ? <span className="text-fg-muted"> · {e.detail}</span> : null}</span>
              <span className="shrink-0 text-xs text-fg-subtle">{formatRelative(e.at)}</span>
            </InfoRow>
          ))}</ul>
        )}
      </Section>
      <Section title="Worker history" icon={<CpuChipIcon className="h-4 w-4" />}>
        {data.workerHistory.length === 0 ? <Empty /> : (
          <ul className="space-y-1">{data.workerHistory.map((e, i) => (
            <InfoRow key={i}>
              <span className="text-fg">{e.label}{e.detail ? <span className="text-fg-muted"> · {e.detail}</span> : null}</span>
              <span className="shrink-0 text-xs text-fg-subtle">{formatRelative(e.at)}</span>
            </InfoRow>
          ))}</ul>
        )}
      </Section>
      <Section title="Retry history" icon={<ArrowPathIcon className="h-4 w-4" />}>
        {data.retryHistory.length === 0 ? <Empty label="No retries." /> : (
          <ul className="space-y-1">{data.retryHistory.map((r) => (
            <InfoRow key={r.attempt}>
              <span className="text-fg">Attempt {r.attempt} <span className="text-fg-muted">· {r.reason}</span></span>
              <span className="shrink-0 text-xs text-fg-subtle">{r.backoff ? `backoff ${r.backoff}` : formatRelative(r.at)}</span>
            </InfoRow>
          ))}</ul>
        )}
      </Section>
    </div>
  );
}

function RelatedTab({ data, onNavigate }: { data: QueueMessageDetail; onNavigate: (to: string) => void }) {
  const senderDomain = domainOf(data.sender);
  return (
    <div className="space-y-6">
      <Section title="Policy decisions" icon={<AdjustmentsHorizontalIcon className="h-4 w-4" />}>
        {data.policyDecisions.length === 0 ? <Empty label="No policy matched." /> : (
          <ul className="space-y-1">{data.policyDecisions.map((p) => (
            <li key={p.id}>
              <NavRow onClick={() => onNavigate(`/policies?policyId=${encodeURIComponent(p.id)}`)}>
                <span className="truncate text-fg">{p.name}</span>
                <span className="shrink-0 text-xs text-fg-muted capitalize">{p.action} · Policy Engine →</span>
              </NavRow>
            </li>
          ))}</ul>
        )}
      </Section>

      <Section title="Spam intelligence" icon={<FunnelIcon className="h-4 w-4" />}>
        {!data.spam ? <Empty /> : (
          <NavRow onClick={() => onNavigate(senderDomain ? `/spam-intelligence?domain=${encodeURIComponent(senderDomain)}` : '/spam-intelligence')}>
            <span className="text-fg capitalize">{data.spam.verdict}{data.spam.reputation ? ` · ${data.spam.reputation}` : ''}</span>
            <span className="shrink-0 font-mono text-xs text-fg-muted">score {data.spam.score.toFixed(1)} →</span>
          </NavRow>
        )}
      </Section>

      <Section title="Threat intelligence" icon={<FireIcon className="h-4 w-4" />}>
        {!data.threat ? <Empty /> : (
          <NavRow onClick={() => onNavigate('/threat-dashboard')}>
            <span className="text-fg">{data.threat.category}</span>
            <span className="shrink-0 text-xs text-fg-muted capitalize">{data.threat.reputation} · Threat Dashboard →</span>
          </NavRow>
        )}
      </Section>

      <Section title="Attachments" icon={<PaperClipIcon className="h-4 w-4" />}>
        {data.relatedAttachments.length === 0 ? <Empty /> : (
          <ul className="space-y-1">{data.relatedAttachments.map((a) => (
            <li key={a.id}>
              <NavRow onClick={() => onNavigate(a.sha256 ? `/attachments?sha256=${encodeURIComponent(a.sha256)}` : '/attachments')}>
                <span className="truncate text-fg">{a.filename}</span>
                <span className="shrink-0 text-xs text-fg-muted">{a.verdict}</span>
              </NavRow>
            </li>
          ))}</ul>
        )}
      </Section>

      <Section title="URLs" icon={<LinkIcon className="h-4 w-4" />}>
        {data.relatedUrls.length === 0 ? <Empty /> : (
          <ul className="space-y-1">{data.relatedUrls.map((u, i) => (
            <InfoRow key={i}>
              {/* Rendered as text, never clickable — may be malicious. */}
              <span className="truncate font-mono text-xs text-fg" title={u.url}>{u.url}</span>
              <span className="shrink-0 text-xs text-fg-muted">{u.reputation}</span>
            </InfoRow>
          ))}</ul>
        )}
      </Section>

      <Section title="Related alerts" icon={<BellAlertIcon className="h-4 w-4" />}>
        {data.relatedAlerts.length === 0 ? <Empty /> : (
          <ul className="space-y-1">{data.relatedAlerts.map((a) => (
            <li key={a.id}>
              <NavRow onClick={() => onNavigate('/security-alerts')}>
                <span className="truncate text-fg">{a.title}</span>
                <span className="shrink-0 text-xs text-fg-muted capitalize">{a.severity} · {a.status}</span>
              </NavRow>
            </li>
          ))}</ul>
        )}
      </Section>
    </div>
  );
}
